import React from 'react';
import { Users, Mail, Calendar } from 'lucide-react';
import { RSVPStats } from '../../../types/invitation';
import ThemedCard from '../../themed/ThemedCard';
import { RSVPResponseWithTable, PhysicalInvitationStats, SeatAssignmentStats } from '../types';

interface OverviewTabProps {
  rsvpStats: RSVPStats;
  confirmationRate: number;
  physicalInvitationStats: PhysicalInvitationStats;
  seatAssignmentStats: SeatAssignmentStats;
  rsvpResponses: RSVPResponseWithTable[];
  dietaryStats: Record<string, number>;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  rsvpStats,
  confirmationRate,
  physicalInvitationStats,
  seatAssignmentStats,
  rsvpResponses,
  dietaryStats,
}) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ThemedCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-wedding-text-title">出席率</h3>
            <span className="text-2xl font-semibold text-emerald-600">{confirmationRate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div className="bg-emerald-500 h-3 rounded-full transition-all duration-500" style={{
              width: `${confirmationRate}%`
            }}></div>
          </div>
          <div className="flex justify-between text-sm text-wedding-text-body">
            <span>{rsvpStats.attending} 出席</span>
            <span>{rsvpStats.total} 邀請</span>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-wedding-text-title">總出席人數</h3>
            <span className="text-2xl font-semibold text-slate-600">{rsvpStats.totalGuests}</span>
          </div>
          <div className="flex items-center">
            <Users className="h-5 w-5 text-slate-500 mr-2" />
            <span className="text-sm text-wedding-text-body">包含同行者</span>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-wedding-text-title">實體喜帖</h3>
            <span className="text-2xl font-semibold text-wedding-pink">{physicalInvitationStats.totalRequests}</span>
          </div>
          <div className="flex items-center">
            <Mail className="h-5 w-5 text-wedding-pink mr-2" />
            <span className="text-sm text-wedding-text-body">需要寄送</span>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <h3 className="font-medium text-wedding-text-title mb-4">狀態分布</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                <span className="text-sm text-wedding-text-body">出席</span>
              </div>
              <span className="text-sm font-medium text-wedding-text-title">{rsvpStats.attending}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-rose-400 rounded-full mr-2"></div>
                <span className="text-sm text-wedding-text-body">無法出席</span>
              </div>
              <span className="text-sm font-medium text-wedding-text-title">{rsvpStats.declined}</span>
            </div>
          </div>
        </ThemedCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ThemedCard className="p-6">
          <h3 className="font-medium text-wedding-text-title mb-4">親友分類</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-wedding-gold rounded-full mr-2"></div>
                <span className="text-sm text-wedding-text-body">男方親友</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-wedding-text-title">{rsvpStats.guestSideStats.groom.count} 組</span>
                <span className="text-xs text-wedding-text-body block">{rsvpStats.guestSideStats.groom.totalGuests} 人 ({rsvpStats.guestSideStats.groom.percentage}%)</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-wedding-pink rounded-full mr-2"></div>
                <span className="text-sm text-wedding-text-body">女方親友</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-wedding-text-title">{rsvpStats.guestSideStats.bride.count} 組</span>
                <span className="text-xs text-wedding-text-body block">{rsvpStats.guestSideStats.bride.totalGuests} 人 ({rsvpStats.guestSideStats.bride.percentage}%)</span>
              </div>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <h3 className="font-medium text-wedding-text-title mb-4">座位分配進度</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-wedding-text-body">已分配座位</span>
              <span className="text-sm font-medium text-wedding-text-title">
                {seatAssignmentStats.assignedSeats} / {seatAssignmentStats.totalAttending}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${seatAssignmentStats.assignmentRate}%` }}
              />
            </div>
            <div className="text-center">
              <span className="text-lg font-bold text-teal-600">{seatAssignmentStats.assignmentRate}%</span>
              <span className="text-sm text-wedding-text-body ml-1">完成率</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center p-3 bg-teal-50 rounded-lg">
                <div className="text-lg font-semibold text-teal-600">{seatAssignmentStats.assignedSeats}</div>
                <div className="text-xs text-wedding-text-body">已安排</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-wedding-text-body">{seatAssignmentStats.unassignedSeats}</div>
                <div className="text-xs text-wedding-text-body">未安排</div>
              </div>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <h3 className="font-medium text-wedding-text-title mb-4">最新回覆</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {rsvpResponses.slice(0, 5).map((response) => (
              <div key={response.id} className="flex items-center justify-between p-3 bg-wedding-bg/50 rounded-lg">
                <div>
                  <div className="font-medium text-wedding-text-title">{response.guestName}</div>
                  <div className="text-sm text-wedding-text-body">
                    {response.submittedAt.toLocaleDateString('zh-TW')}
                  </div>
                </div>
                <div className="flex items-center">
                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                    response.rsvpData.attending === 'yes'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-rose-100 text-rose-800'
                  }`}>
                    {response.rsvpData.attending === 'yes' ? '出席' : '無法出席'}
                  </span>
                  {response.rsvpData.attending === 'yes' && response.rsvpData.guestCount > 1 && (
                    <span className="ml-2 text-xs text-wedding-text-body">+{response.rsvpData.guestCount - 1}</span>
                  )}
                </div>
              </div>
            ))}
            {rsvpResponses.length === 0 && (
              <div className="text-center py-8 text-wedding-text-body">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-wedding-text-body" />
                <p>尚無回覆記錄</p>
              </div>
            )}
          </div>
        </ThemedCard>
      </div>

      {Object.keys(dietaryStats).length > 0 && (
        <ThemedCard className="p-6">
          <h3 className="font-medium text-wedding-text-title mb-4">特殊需求統計</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(dietaryStats).map(([type, count]) => (
              <div key={type} className="text-center p-4 bg-teal-50 rounded-lg">
                <div className="text-2xl font-bold text-teal-600">{count}</div>
                <div className="text-sm text-wedding-text-body">{type}</div>
              </div>
            ))}
          </div>
        </ThemedCard>
      )}
    </>
  );
};
